import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { reviewKeys } from '@/lib/queryKeys';
import { reviewPlatformsV2Endpoints } from '@/lib/api/endpoints';
import { apiPost, operationsClient } from '@/lib/api';
import type { PostReplyDto } from '@/types/review';
import { invalidateReviewViews, invalidateReviewViewsAndStats } from './invalidateReviewViews';

interface ReplyVariables {
  /** Raw `ExternalReview.id` (no `ext_` prefix). */
  externalReviewId: string;
  dto: PostReplyDto;
}

/**
 * `POST /v2/review-platforms/replies/:id/post` — submit the human-approved
 * reply text to the platform (Google + Facebook only). Backend handles the
 * actual write call + retry with exponential backoff.
 */
export function useReplyToReview() {
  const queryClient = useQueryClient();

  return useApiMutation<unknown, ReplyVariables>({
    mutationFn: ({ externalReviewId, dto }) =>
      apiPost<unknown, PostReplyDto>(
        operationsClient,
        reviewPlatformsV2Endpoints.replies.post(externalReviewId),
        dto,
      ),
    onSuccess: (_, variables) => {
      // Invalidate the unified list (changes the row's replyStatus chip) and
      // any open detail-card cache.
      invalidateReviewViewsAndStats(queryClient);
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.detail('external', variables.externalReviewId),
      });
      toast.success('Reply posted');
    },
  });
}

/**
 * `POST /v2/review-platforms/replies/:id/mark-handled` — flag a non-API row
 * (Yelp/BBB/Thumbtack) as handled out-of-band.
 */
export function useMarkReplyHandled() {
  const queryClient = useQueryClient();

  return useApiMutation<unknown, { externalReviewId: string }>({
    mutationFn: ({ externalReviewId }) =>
      apiPost<unknown>(
        operationsClient,
        reviewPlatformsV2Endpoints.replies.markHandled(externalReviewId),
      ),
    onSuccess: (_, variables) => {
      // Stats too, because `markHandled()` is not restricted to the platforms
      // its docblock names.
      //
      // `ReplyPosterService.markHandled` looks the review up by id and calls
      // `recordReplyPosted` with no platform guard — "Used for Yelp / BBB /
      // Thumbtack" describes intent, not an enforced constraint. So a Google or
      // Facebook review can be marked handled, and that writes the exact
      // `replyStatus` / `replyPostedAt` columns `responseRate` and
      // `medianTimeToReplyHours` read.
      //
      // Scoping this to the lists left the strip stale after that happens, and
      // a stale figure is indistinguishable from a correct one. Raised in
      // review on #2434.
      invalidateReviewViewsAndStats(queryClient);
      void queryClient.invalidateQueries({
        queryKey: reviewKeys.detail('external', variables.externalReviewId),
      });
      toast.success('Marked handled');
    },
  });
}
